/**
 * Configuración i18n con `i18next` + `react-i18next`.
 *
 * Idiomas previstos:
 *
 *   - `es-ES` (default, idioma de origen del curso)
 *   - `en-US` (placeholder — UI ya traducible; contenido del curso
 *     sigue solo en español hasta que se decida traducir)
 *
 * Estrategia de implementación:
 *
 *   - **Strings de UI** (botones, labels, mensajes del shell) viven en
 *     `locales/<lang>/common.json` y se consumen con `useTranslation()`.
 *   - **Contenido del curso** (teoría, quiz, labs, recursos) sigue en
 *     su markdown nativo. Cuando llegue la decisión de traducir el
 *     curso, se hará produciendo `cursos/<slug>-<lang>/` o
 *     `cursos/<slug>/i18n/<lang>/`, no via i18n strings.
 *
 * Detección de idioma: i18next-browser-languagedetector mira en
 * `localStorage` (`mw-language`) y, si no hay nada, en
 * `navigator.language`. Fallback a `es` siempre.
 *
 * Reutilización: cualquier shell PV-Learn lo hereda con import al inicio
 * de `main.tsx`. Para añadir un idioma nuevo, basta con crear el archivo
 * JSON correspondiente y registrarlo en `resources`.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import es from './locales/es/common.json'
import en from './locales/en/common.json'
import esDiagnostic from './locales/es/diagnostic.json'
import enDiagnostic from './locales/en/diagnostic.json'
import esMw from './locales/es/mw.json'
import enMw from './locales/en/mw.json'

export const SUPPORTED_LANGUAGES = ['es', 'en'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  es: 'Español',
  en: 'English',
}

// MentorWallet: español por defecto. Solo cambia si el usuario lo elige
// explícitamente. Si hay un valor 'en' guardado de una sesión anterior
// (antes del fix D.12), lo limpiamos para forzar español.
const savedLang = typeof window !== 'undefined'
  ? localStorage.getItem('mw-language')
  : null

// Limpiar localStorage stale: si el dominio es MW y el idioma guardado
// es 'en' sin que el usuario lo haya elegido conscientemente (no hay
// flag explícito), reseteamos a español.
const isMwDomain = typeof window !== 'undefined' &&
  window.location.hostname.includes('mentor-wallet')
if (isMwDomain && savedLang === 'en') {
  // Solo limpiar si no hay flag de elección explícita
  const wasExplicit = typeof window !== 'undefined' &&
    localStorage.getItem('mw-language-explicit') === 'true'
  if (!wasExplicit) {
    localStorage.removeItem('mw-language')
  }
}

const effectiveLang = (() => {
  const current = typeof window !== 'undefined'
    ? localStorage.getItem('mw-language')
    : null
  if (current && SUPPORTED_LANGUAGES.includes(current as SupportedLanguage)) {
    return current
  }
  return 'es'
})()

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      es: { common: es, diagnostic: esDiagnostic, mw: esMw },
      en: { common: en, diagnostic: enDiagnostic, mw: enMw },
    },
    lng: effectiveLang,
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    defaultNS: 'common',
    ns: ['common', 'diagnostic', 'mw'],
    interpolation: { escapeValue: false },
    returnNull: false,
  })

export default i18n

/** Cambia el idioma, lo persiste y recarga para que todo se actualice. */
export function changeLanguage(lang: SupportedLanguage) {
  localStorage.setItem('mw-language', lang)
  localStorage.setItem('mw-language-explicit', 'true')
  void i18n.changeLanguage(lang)
  // Reload para que el content loader (que lee localStorage al boot)
  // sirva los archivos .en.md correctos y todo se re-renderice.
  window.location.reload()
}
